import type { TwoFactorMethod } from '@qlk/shared';
import { TWO_FACTOR_LABEL, TWO_FACTOR_METHODS } from '@qlk/shared';
import { useState } from 'react';

import { HopThoai, Nut, OChon } from '../../../ui/index.js';
import { useDanhSachKenhGon, useLuuTaiKhoanGoogle, type TaiKhoanGoogleDong } from '../api.js';
import { ONhieuKenh } from './ONhieuKenh.js';

export interface DialogTaiKhoanGoogleProps {
  /** Không truyền = thêm mới. */
  banGhi?: TaiKhoanGoogleDong;
  kenhDuPhong: { id: string; name: string }[];
  onDong: () => void;
}

/** `YYYY-MM-DD` cho ô input type="date". */
function ngayInput(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

export function DialogTaiKhoanGoogle({ banGhi, kenhDuPhong, onDong }: DialogTaiKhoanGoogleProps) {
  const danhSachKenh = useDanhSachKenhGon();
  const luu = useLuuTaiKhoanGoogle();

  const [label, setLabel] = useState(banGhi?.label ?? '');
  const [twoFactorMethod, setTwoFactorMethod] = useState<TwoFactorMethod>(
    banGhi?.twoFactorMethod ?? 'none',
  );
  const [recoveryEmailSet, setRecoveryEmailSet] = useState(banGhi?.recoveryEmailSet ?? false);
  const [ghiChu, setGhiChu] = useState(banGhi?.suspiciousLoginNote ?? '');
  const [raSoat, setRaSoat] = useState(ngayInput(banGhi?.lastReviewedAt));
  const [chon, setChon] = useState<string[]>(banGhi?.channelIds ?? []);

  const kenh = danhSachKenh.data?.items.map((k) => ({ id: k.id, name: k.name })) ?? kenhDuPhong;

  // Schema phía máy chủ từ chối email đầy đủ: nhãn bắt buộc có ký tự che `•`.
  const daChe = label.includes('•');
  const hopLe = label.trim().length >= 5 && daChe;

  const tieuDe = banGhi ? 'Sửa tài khoản Google' : 'Thêm tài khoản Google';

  return (
    <HopThoai
      title={tieuDe}
      rong="md"
      onDong={onDong}
      chanHop={
        <>
          {luu.isError && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {luu.error.message}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            disabled={!hopLe}
            dangChay={luu.isPending}
            onClick={() => {
              luu.mutate(
                {
                  ...(banGhi ? { id: banGhi.id } : {}),
                  input: {
                    label: label.trim(),
                    channelIds: chon,
                    twoFactorMethod,
                    recoveryEmailSet,
                    suspiciousLoginNote: ghiChu.trim() || null,
                    lastReviewedAt: raSoat
                      ? new Date(`${raSoat}T00:00:00+07:00`).toISOString()
                      : null,
                  },
                },
                { onSuccess: onDong },
              );
            }}
          >
            Lưu
          </Nut>
        </>
      }
    >
      <p className="text-phu text-chu-mo">
        Chỉ ghi trạng thái bảo mật. Không nhập mật khẩu, mã khôi phục hay mã 2FA.
      </p>

      <div className="mt-4 space-y-4">
        <div>
          <label htmlFor="tk-label" className="mb-1 block text-phu font-medium text-chu-phu">
            Email đã che
          </label>
          <input
            id="tk-label"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);
            }}
            className="vung-bam w-full rounded-[10px] border border-vien px-3 text-co-ban text-chu"
            placeholder="abc••••@gmail.com"
          />
          {!daChe && label.length > 0 && (
            <p className="mt-1 text-phu text-bad-chu">
              Phải che bớt email bằng ký tự • (ví dụ abc••••@gmail.com).
            </p>
          )}
        </div>

        <OChon
          hienNhan
          label="Phương thức 2FA"
          value={twoFactorMethod}
          onChange={(v) => {
            setTwoFactorMethod(v as TwoFactorMethod);
          }}
          options={TWO_FACTOR_METHODS.map((m) => ({ value: m, label: TWO_FACTOR_LABEL[m] }))}
          className="w-full"
        />

        <label className="flex items-center gap-3 text-co-ban text-chu">
          <input
            type="checkbox"
            className="size-4"
            checked={recoveryEmailSet}
            onChange={(e) => {
              setRecoveryEmailSet(e.target.checked);
            }}
          />
          Đã đặt email khôi phục
        </label>

        <div>
          <label htmlFor="tk-ra-soat" className="mb-1 block text-phu font-medium text-chu-phu">
            Rà soát gần nhất
          </label>
          <input
            id="tk-ra-soat"
            type="date"
            value={raSoat}
            onChange={(e) => {
              setRaSoat(e.target.value);
            }}
            className="vung-bam w-full rounded-[10px] border border-vien px-3 text-co-ban text-chu"
          />
        </div>

        <div>
          <label htmlFor="tk-ghi-chu" className="mb-1 block text-phu font-medium text-chu-phu">
            Ghi chú đăng nhập nghi vấn
          </label>
          <textarea
            id="tk-ghi-chu"
            rows={3}
            value={ghiChu}
            onChange={(e) => {
              setGhiChu(e.target.value);
            }}
            className="w-full rounded-[10px] border border-vien p-3 text-co-ban text-chu"
          />
        </div>

        <fieldset>
          <legend className="mb-2 text-phu font-medium text-chu-phu">Kênh quản lý</legend>
          <ONhieuKenh
            kenh={kenh}
            chon={chon}
            onDoi={(id) => {
              setChon((truoc) =>
                truoc.includes(id) ? truoc.filter((x) => x !== id) : [...truoc, id],
              );
            }}
            onDatLai={setChon}
          />
        </fieldset>
      </div>
    </HopThoai>
  );
}
